import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, BookOpen, CalendarDays, CheckCircle2, Database, Download, HelpCircle, LayoutDashboard, Mail, Search, Upload } from 'lucide-react';
import './SupportPage.css';

const helpTopics = [
  {
    icon: LayoutDashboard,
    title: 'Dashboards',
    text: 'Create focused college lists, add schools, customize columns, and track application details in one place.',
  },
  {
    icon: CheckCircle2,
    title: 'Progress',
    text: 'Use General Progress for shared application steps and Individual Progress for each final college.',
  },
  {
    icon: CalendarDays,
    title: 'Calendar',
    text: 'Use dated tasks and college deadlines to see what is coming up across your application plan.',
  },
  {
    icon: Database,
    title: 'College Data',
    text: 'College facts come from public government data, not AI-generated guesses.',
  },
];

const faqs = [
  {
    question: 'What is the difference between General Progress and Individual Progress?',
    answer: 'General Progress is for work shared across applications, such as your main essay, recommendation letters, FAFSA, CSS Profile, and score sends. Individual Progress is for one college at a time: its deadline, supplements, portal checks, tasks, reminders, and submitted status.',
  },
  {
    question: 'What appears in my Calendar?',
    answer: 'Calendar shows recommended dates from General Progress plus dated tasks and application deadlines from your final colleges. Edit a suggested General Progress date whenever your own schedule is different.',
  },
  {
    question: 'Why did a college appear in Progress?',
    answer: 'Progress shows the colleges you added under Final College List. Dashboards are for researching and comparing colleges; Progress is for the schools you are actively applying to.',
  },
  {
    question: 'Why is a college showing as past due?',
    answer: 'A college shows as past due when its apply-by date has passed and it has not been marked submitted in Progress.',
  },
  {
    question: 'How do I stop deadline reminders for a submitted application?',
    answer: 'Open Progress and use the Mark submitted control on that college card. Submitted applications will no longer show deadline warnings.',
  },
  {
    question: 'Can I build Progress manually instead of from a dashboard?',
    answer: 'Yes. Progress is built from the Final College List tab. Add the schools you are actively applying to there, then track deadlines and tasks in Individual Progress.',
  },
  {
    question: 'Where does the college information come from?',
    answer: 'College facts like school name, location, cost, admissions, testing, and financial aid data come from the U.S. Department of Education College Scorecard API. MaiColleges is not asking AI to invent college data each time you search; it uses public government data and your own saved planning notes.',
  },
  {
    question: 'Where do my tasks and dashboards save?',
    answer: 'Your planning data is stored with your signed-in account in Firestore and cached on this device for faster loading. Signing in on another device loads the data connected to that same account.',
  },
  {
    question: 'What should I put on a dashboard?',
    answer: 'Use dashboards for research and comparison: possible schools, costs, fit notes, admissions details, portal links, financial aid, and anything you are still deciding. Move to Progress when a school becomes part of your final application plan.',
  },
  {
    question: 'How do I import a spreadsheet?',
    answer: 'In Add Dashboard, choose Import CSV beside Start from Scratch. Excel, Google Sheets, and Numbers can all download or export a sheet as CSV. MaiColleges shows a preview before creating the dashboard.',
  },
  {
    question: 'What happens to imported column names?',
    answer: 'MaiColleges ignores capitalization, extra spaces, hyphens, and underscores when matching headers. A matching name uses the existing MaiColleges column. Any unfamiliar header becomes a Custom Field that remains available in the dashboard’s Columns menu.',
  },
  {
    question: 'How do I add or edit text in a Custom Field?',
    answer: 'Double-click a Custom Field cell in your dashboard to start typing. Press Enter or click outside the cell to save your change.',
  },
  {
    question: 'How do I export a dashboard?',
    answer: 'Open a dashboard and use Export directly beside Expand Table. Excel creates an .xlsx workbook, CSV creates a simple spreadsheet file, and PDF opens a landscape print view that can be saved as a PDF. The export follows the table’s selected columns, filters, and sorting.',
  },
  {
    question: 'Where are the college application guides?',
    answer: 'Home has practical Resources for the application timeline, essay planning, building a college list, and choosing dashboard templates. The guides also explain which MaiColleges tool to use for each step.',
  },
  {
    question: 'Why are extracurriculars and awards set up this way?',
    answer: 'The extracurricular and awards sections are organized in a Common App-like format so you can practice collecting the details colleges usually ask for before you are filling out real applications.',
  },
  {
    question: 'How do reminders work?',
    answer: 'General Progress steps and individual college tasks can use dated reminders. Application deadline nudges are based on each college’s apply-by date unless that application is marked submitted.',
  },
];

const resourceLinks = [
  { title: 'Dashboard Template Guide', text: 'See what every template tracks and when to use it.', to: '/dashboard-template-guide', icon: LayoutDashboard },
  { title: 'Application Timeline', text: 'Plan shared steps, deadlines, financial aid, and submission follow-up.', to: '/application-timeline', icon: CalendarDays },
  { title: 'Essay Game Plan', text: 'Start early and organize drafting, feedback, and revision.', to: '/essay-game-plan', icon: BookOpen },
  { title: 'Build Your College List', text: 'Narrow a balanced list around fit, cost, and real interest.', to: '/college-list-guide', icon: CheckCircle2 },
];

const workflowItems = [
  {
    label: 'Dashboard',
    text: 'Your working college lists live here. Add schools, compare details, and keep custom notes or tracking columns.',
  },
  {
    label: 'Progress',
    text: 'General Progress tracks work shared across applications. Individual Progress tracks deadlines, tasks, and submitted status for each school in your Final College List.',
  },
  {
    label: 'Calendar',
    text: 'Calendar reads General Progress target dates plus dated tasks and apply-by dates from your final colleges.',
  },
  {
    label: 'Notifications',
    text: 'Reminders and upcoming deadline alerts show in the notification sidebar when they are due.',
  },
];

function SupportPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const visibleFaqs = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return faqs;
    return faqs.filter((item) => `${item.question} ${item.answer}`.toLowerCase().includes(query));
  }, [searchTerm]);

  return (
    <main className="support-page">
      <div className="support-page__inner">
        <header className="support-page__header">
          <div>
            <span className="support-page__eyebrow">Help Center</span>
            <h1>Support</h1>
            <p>Find quick answers for planning, resources, dashboards, imports, exports, progress, and reminders.</p>
          </div>
        </header>

        <section className="support-page__search" aria-label="Support search">
          <Search size={18} strokeWidth={1.7} />
          <input
            type="search"
            placeholder="Search support topics"
            aria-label="Search support topics"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </section>

        <section className="support-page__resources" aria-labelledby="support-resources-title">
          <div className="support-page__section-header">
            <BookOpen size={18} strokeWidth={1.7} />
            <h2 id="support-resources-title">Guides and Resources</h2>
          </div>
          <div className="support-page__resource-grid">
            {resourceLinks.map(({ title, text, to, icon: Icon }) => (
              <Link to={to} className="support-resource" key={to}>
                <Icon size={17} strokeWidth={1.7} />
                <span><strong>{title}</strong><small>{text}</small></span>
              </Link>
            ))}
          </div>
        </section>

        <section className="support-page__transfer" aria-label="Import and export dashboards">
          <article>
            <Upload size={18} strokeWidth={1.7} />
            <div><h2>Import CSV</h2><p>Create a dashboard from Excel, Google Sheets, or Numbers. Unmatched headers become Custom Fields.</p></div>
          </article>
          <article>
            <Download size={18} strokeWidth={1.7} />
            <div><h2>Export dashboards</h2><p>Download the current table as Excel or CSV, or save a landscape PDF copy.</p></div>
          </article>
        </section>

        <section className="support-page__topics" aria-label="Popular support topics">
          {helpTopics.map(({ icon: Icon, title, text }) => (
            <article className="support-topic" key={title}>
              <div className="support-topic__icon">
                <Icon size={19} strokeWidth={1.7} />
              </div>
              <div>
                <h2>{title}</h2>
                <p>{text}</p>
              </div>
            </article>
          ))}
        </section>

        <section className="support-page__workflow" aria-labelledby="workflow-title">
          <div className="support-page__section-header">
            <Bell size={18} strokeWidth={1.7} />
            <h2 id="workflow-title">How MaiColleges Connects Your Work</h2>
          </div>
          <div className="support-page__workflow-grid">
            {workflowItems.map((item, index) => (
              <article className="support-flow" key={item.label}>
                <span>{index + 1}</span>
                <div>
                  <h3>{item.label}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <div className="support-page__layout">
          <section className="support-page__panel" aria-labelledby="faq-title">
            <div className="support-page__section-header">
              <HelpCircle size={18} strokeWidth={1.7} />
              <h2 id="faq-title">Common Questions</h2>
            </div>

            <div className="support-page__faq-list">
              {visibleFaqs.map((item) => (
                <details className="support-faq" key={item.question}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
              {visibleFaqs.length === 0 && (
                <div className="support-page__no-results">No matching help topics yet. Try a shorter search.</div>
              )}
            </div>
          </section>

          <aside className="support-page__contact" aria-label="Contact support">
            <div className="support-page__contact-icon">
              <Mail size={20} strokeWidth={1.7} />
            </div>
            <h2>Still need help?</h2>
            <p>
              Tell the team what page you were on, what you expected, and what happened instead.
            </p>
            <a href="/contact">Contact Us</a>
          </aside>
        </div>
      </div>
    </main>
  );
}

export default SupportPage;
